import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import { BuildingReadModel } from '@client/api';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DD,
  DT,
  formatMatrikkel,
  GridCol,
  GridRow,
  isDefinedNumber,
  isDefinedString,
  toNumberString,
  toPriceWithIntervalString,
} from '@client/toolkit';

import { formatHeatingGrade } from '../../../heating-grade.util';

export interface BuildingInformationCardProps {
  className?: string;
  building?: BuildingReadModel;
  isFetching?: boolean;
  variant?: 'default' | 'compact';
}

export function BuildingInformationCard({ className, building, isFetching, variant = 'default' }: BuildingInformationCardProps) {
  const { t, ready } = useTranslation('rentalObjects');
  const { t: tType } = useTranslation('type');
  const { t: tGlobal } = useTranslation('global');

  // Matrikkel ("3203-29/190"). Formatting is shared with the reporting pages via the
  // formatMatrikkel toolkit helper so the kommune-gnr/bnr convention lives in one place.
  const matrikkel = formatMatrikkel(building?.cadastralInformation);

  if (variant === 'compact') {
    return (
      <div className={classNames('border border-border rounded-md px-4 py-3', className)}>
        <div className="uppercase font-bold text-xs mb-4 text-white">
          {t('building.buildingInformation')}
        </div>
        <div className="grid grid-cols-3 gap-y-2 text-xs leading-relaxed">
          {/* Column 1 */}
          <div className="space-y-1 pr-6">
            {/* COMMENTED OUT: Brukertype - uncomment to restore
            <div className="flex justify-between gap-2">
              <span className="text-white">{tGlobal('building.usageType')}</span>
              <span className="text-white/60">
                {isDefinedString(building?.usageType) ? tType(`UsageType.${building?.usageType}`) : '-'}
              </span>
            </div>
            */}
            <div className="flex justify-between gap-2">
              <span className="text-white">{tGlobal('building.buildingType')}</span>
              <span className="text-white/60">
                {isDefinedString(building?.type) ? tType(`BuildingType.${building?.type}`) : '-'}
              </span>
            </div>
            <div className="flex justify-between gap-2">
              <span className="text-white">{tGlobal('building.buildingNumber')}</span>
              <span className="text-white/60">
                {isDefinedString(building?.buildingNumber) ? building?.buildingNumber : '-'}
              </span>
            </div>
            <div className="flex justify-between gap-2">
              <span className="text-white">{tGlobal('building.matrikkel')}</span>
              <span className="text-white/60">
                {isDefinedString(matrikkel) ? matrikkel : '-'}
              </span>
            </div>
          </div>

          {/* Column 2 */}
          <div className="space-y-1 px-6 border-x border-border">
            <div className="flex justify-between gap-2">
              <span className="text-white">{tGlobal('building.energyLabel')}</span>
              <span className="text-brand-primary">
                {isDefinedString(building?.energyLabel) ? building?.energyLabel : '-'}
              </span>
            </div>
            <div className="flex justify-between gap-2">
              <span className="text-white">{tGlobal('building.heatingGrade')}</span>
              <span className="text-white/60">
                {isDefinedString(building?.heatingGrade) ? formatHeatingGrade(building?.heatingGrade, tGlobal) : '-'}
              </span>
            </div>
            <div className="flex justify-between gap-2">
              <span className="text-white">{tGlobal('space.standard')}</span>
              <span className="text-white/60">
                {isDefinedString(building?.standard) ? tType(`StandardType.${building?.standard}`) : '-'}
              </span>
            </div>
          </div>

          {/* Column 3 */}
          <div className="space-y-1 pl-6">
            <div className="flex justify-between gap-2">
              <span className="text-white">{tGlobal('building.constructionYear')}</span>
              <span className="text-white/60">
                {isDefinedNumber(building?.cadastralInformation?.constructionYear) ? building?.cadastralInformation?.constructionYear : '-'}
              </span>
            </div>
            <div className="flex justify-between gap-2">
              <span className="text-white">{tGlobal('building.energyCertificate')}</span>
              <span className="text-white/60">
                {isDefinedString(building?.energyCertificateUrl) ? (
                  <a
                    href={building?.energyCertificateUrl ?? undefined}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-brand-primary hover:underline"
                  >
                    {tGlobal('building.energyCertificateView')}
                  </a>
                ) : (
                  '-'
                )}
              </span>
            </div>
            <div className="flex justify-between gap-2">
              <span className="text-white">{t('buildingPackages.label')}</span>
              <span className="text-white/60">
                {building?.packages?.filter((p) => p.isActive).length
                  ? building.packages
                      .filter((p) => p.isActive)
                      .map((p) => `${t(`buildingPackages.${p.packageType?.toLowerCase()}`)} ${t(`buildingPackages.${p.tier?.toLowerCase()}`)}`)
                      .join(', ')
                  : '-'}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Card className={className}>
      <GridRow cols={3} className="divide-x">
        <GridCol span={{ def: 3, sm: 2 }}>
          <CardHeader>
            <CardTitle className="flex-1" variant="dense" loading={!ready}>
              {t('building.buildingInformation')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex flex-col py-2">
                <GridRow cols={2} spacingY={1} spacingX={2}>
                  {/* COMMENTED OUT: Brukertype - uncomment to restore
                  <GridCol span={1}>
                    <DT className="w-1/2">{tGlobal('building.usageType')}</DT>
                  </GridCol>
                  <GridCol span={1}>
                    <DD loading={isFetching} isEmpty={!isDefinedString(building?.usageType)}>
                      {tType(`UsageType.${building?.usageType}`)}
                    </DD>
                  </GridCol>
                  */}

                  <GridCol span={1}>
                    <DT className="w-1/2">{tGlobal('building.buildingType')}</DT>
                  </GridCol>
                  <GridCol span={1}>
                    <DD loading={isFetching} isEmpty={!isDefinedString(building?.type)}>
                      {tType(`BuildingType.${building?.type}`)}
                    </DD>
                  </GridCol>

                  <GridCol span={1}>
                    <DT className="w-1/2">{tGlobal('building.buildingNumber')}</DT>
                  </GridCol>
                  <GridCol span={1}>
                    <DD loading={isFetching} isEmpty={!isDefinedString(building?.buildingNumber)}>
                      {building?.buildingNumber}
                    </DD>
                  </GridCol>

                  <GridCol span={1}>
                    <DT className="w-1/2">{tGlobal('building.matrikkel')}</DT>
                  </GridCol>
                  <GridCol span={1}>
                    <DD loading={isFetching} isEmpty={!isDefinedString(matrikkel)}>
                      {matrikkel}
                    </DD>
                  </GridCol>

                  <GridCol span={1}>
                    <DT>{tGlobal('building.energyLabel')}</DT>
                  </GridCol>
                  <GridCol span={1}>
                    <DD
                      loading={isFetching}
                      isEmpty={!isDefinedString(building?.energyLabel)}
                      variantColor="none"
                      className="text-brand-primary"
                    >
                      {building?.energyLabel}
                    </DD>
                  </GridCol>

                  <GridCol span={1}>
                    <DT>{tGlobal('building.heatingGrade')}</DT>
                  </GridCol>
                  <GridCol span={1}>
                    <DD loading={isFetching} isEmpty={!isDefinedString(building?.heatingGrade)}>
                      {formatHeatingGrade(building?.heatingGrade, tGlobal)}
                    </DD>
                  </GridCol>

                  <GridCol span={1}>
                    <DT>{tGlobal('space.standard')}</DT>
                  </GridCol>
                  <GridCol span={1}>
                    <DD loading={isFetching} isEmpty={!isDefinedString(building?.standard)}>
                      {tType(`StandardType.${building?.standard}`)}
                    </DD>
                  </GridCol>

                  <GridCol span={1}>
                    <DT>{tGlobal('building.constructionYear')}</DT>
                  </GridCol>
                  <GridCol span={1}>
                    <DD
                      loading={isFetching}
                      isEmpty={!isDefinedNumber(building?.cadastralInformation?.constructionYear)}
                    >
                      {building?.cadastralInformation?.constructionYear}
                    </DD>
                  </GridCol>

                  <GridCol span={1}>
                    <DT>{tGlobal('building.energyCertificate')}</DT>
                  </GridCol>
                  <GridCol span={1}>
                    <DD loading={isFetching} isEmpty={!isDefinedString(building?.energyCertificateUrl)}>
                      <a
                        href={building?.energyCertificateUrl ?? undefined}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-brand-primary hover:underline"
                      >
                        {tGlobal('building.energyCertificateView')}
                      </a>
                    </DD>
                  </GridCol>

                  {building?.usageType === 'Mixed' && (
                    <>
                      <GridCol span={1}>
                        <DT>{tGlobal('building.commercialArea')}</DT>
                      </GridCol>
                      <GridCol span={1}>
                        <DD loading={isFetching} isEmpty={!isDefinedNumber(building?.commercialArea?.metric)}>
                          {toNumberString(building?.commercialArea?.metric ?? undefined, undefined, {
                            unit: tType('AreaMeasurementUnit.SquareMeters', { context: 'short' }),
                          })}
                        </DD>
                      </GridCol>

                      <GridCol span={1}>
                        <DT>{tGlobal('building.residentialArea')}</DT>
                      </GridCol>
                      <GridCol span={1}>
                        <DD loading={isFetching} isEmpty={!isDefinedNumber(building?.residentialArea?.metric)}>
                          {toNumberString(building?.residentialArea?.metric ?? undefined, undefined, {
                            unit: tType('AreaMeasurementUnit.SquareMeters', { context: 'short' }),
                          })}
                        </DD>
                      </GridCol>
                    </>
                  )}

                  <GridCol span={1}>
                    <DT>{tGlobal('building.area')}</DT>
                  </GridCol>
                  <GridCol span={1}>
                    <DD loading={isFetching} isEmpty={!isDefinedNumber(building?.area?.metric)}>
                      {toNumberString(building?.area?.metric ?? undefined, undefined, {
                        unit: tType('AreaMeasurementUnit.SquareMeters', { context: 'short' }),
                      })}
                    </DD>
                  </GridCol>

                  <GridCol span={1}>
                    <DT>{tGlobal('building.operationalCost')}</DT>
                  </GridCol>
                  <GridCol span={1}>
                    <DD
                      loading={isFetching}
                      isEmpty={!isDefinedString(toPriceWithIntervalString(building?.operationalCost))}
                    >
                      {toPriceWithIntervalString(building?.operationalCost)}
                    </DD>
                  </GridCol>
                </GridRow>
              </div>
            </div>
          </CardContent>
        </GridCol>
      </GridRow>
    </Card>
  );
}
